import React from 'react';
import { Player } from '../api/players.api';

interface PlayerProfileModalProps {
  player: Player | null;
  show: boolean;
  onHide: () => void;
}

const PlayerProfileModal: React.FC<PlayerProfileModalProps> = ({ player, show, onHide }) => {
  if (!player) return null;

  return (
    <>
      {/* Backdrop */}
      {show && (
        <div
          className="modal-backdrop fade show"
          onClick={onHide}
          style={{ zIndex: 1040 }}
        ></div>
      )}
      
      {/* Modal */}
      <div
        className={`modal fade ${show ? 'show' : ''}`}
        style={{ 
          display: show ? 'block' : 'none',
          zIndex: 1050,
          paddingRight: show ? '17px' : '0'
        }}
        tabIndex={-1}
        aria-labelledby="playerProfileModalLabel"
        aria-hidden={!show}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onHide();
          }
        }}
      >
        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" style={{ margin: '1rem' }}>
          <div className="modal-content card-surface" style={{ zIndex: 1051, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header border-bottom pb-3 flex-shrink-0">
              <h5 className="modal-title fw-bold h4 mb-0" id="playerProfileModalLabel" style={{ fontSize: 'clamp(1rem, 3vw, 1.5rem)' }}>
                Player Profile
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onHide}
                aria-label="Close"
                style={{ minWidth: '44px', minHeight: '44px' }}
              ></button>
            </div>
            <div className="modal-body p-3 p-md-4 flex-grow-1 overflow-auto">
              {/* Compact Header with Avatar and Basic Info */}
              <div className="d-flex flex-column flex-md-row align-items-center align-items-md-start gap-3 gap-md-4 mb-4 pb-3 border-bottom">
                {player.thumbnail ? (
                  <img
                    src={player.thumbnail}
                    alt={player.name}
                    className="rounded-circle flex-shrink-0"
                    style={{
                      width: 'clamp(80px, 20vw, 100px)',
                      height: 'clamp(80px, 20vw, 100px)',
                      objectFit: 'cover',
                      border: '3px solid #ffffff',
                      boxShadow: '0 4px 12px rgba(15, 23, 42, 0.1)',
                    }}
                  />
                ) : (
                  <div
                    className="rounded-circle bg-dark d-flex align-items-center justify-content-center text-white flex-shrink-0"
                    style={{
                      width: 'clamp(80px, 20vw, 100px)',
                      height: 'clamp(80px, 20vw, 100px)',
                      fontSize: 'clamp(2rem, 5vw, 2.5rem)',
                      border: '3px solid #ffffff',
                      boxShadow: '0 4px 12px rgba(15, 23, 42, 0.1)',
                    }}
                  >
                    {player.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-grow-1 w-100 text-center text-md-start d-flex flex-column align-items-center align-items-md-start">
                  <h4 className="fw-bold mb-2 mb-md-3 text-white w-100" style={{ fontSize: 'clamp(1.1rem, 3vw, 1.5rem)' }}>
                    {player.name}
                  </h4>
                  <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-md-start w-100">
                    {player.playerId ? (
                      <span className="badge bg-success text-white px-3 py-2 fw-bold">
                        ID: {player.playerId}
                      </span>
                    ) : (
                      <span className="badge bg-secondary text-white px-3 py-2 fw-bold" title="Player ID not available from backend">
                        ID: Not Available
                      </span>
                    )}
                    <span className="badge bg-warning text-white px-3 py-2">
                      {player.role}
                    </span>
                    <span className="badge bg-light text-white border px-3 py-2">
                      {player.district}
                    </span>
                    <span className="badge bg-info text-white px-3 py-2">
                      {player.age ?? 'N/A'} years
                    </span>
                  </div>
                </div>
              </div>

              {/* Compact Details Grid */}
              <div className="row g-3 mb-4">
                <div className="col-6 col-md-6">
                  <div className="small text-muted-soft mb-2" style={{ fontSize: '0.875rem' }}>Team</div>
                  <div className="fw-semibold text-white" style={{ fontSize: '1rem', lineHeight: '1.5' }}>
                    {player.currentTeam || player.district || 'N/A'}
                  </div>
                </div>
                <div className="col-6 col-md-6">
                  <div className="small text-muted-soft mb-2" style={{ fontSize: '0.875rem' }}>Batting</div>
                  <div className="fw-semibold text-white" style={{ fontSize: '1rem', lineHeight: '1.5' }}>
                    {player.battingStyle || 'N/A'}
                  </div>
                </div>
                {player.bowlingStyle && (
                  <div className="col-6 col-md-6">
                    <div className="small text-muted-soft mb-2" style={{ fontSize: '0.875rem' }}>Bowling</div>
                    <div className="fw-semibold text-white" style={{ fontSize: '1rem', lineHeight: '1.5' }}>
                      {player.bowlingStyle}
                    </div>
                  </div>
                )}
                {player.jersey_size && (
                  <div className="col-6 col-md-6">
                    <div className="small text-muted-soft mb-2" style={{ fontSize: '0.875rem' }}>Jersey Size</div>
                    <div className="fw-semibold text-white" style={{ fontSize: '1rem', lineHeight: '1.5' }}>
                      {player.jersey_size}
                    </div>
                  </div>
                )}
                {player.cricheroes_profile_link && (
                  <div className="col-12">
                    <div className="small text-muted-soft mb-2" style={{ fontSize: '0.875rem' }}>Cricheroes Profile</div>
                    <div>
                      <a
                        href={player.cricheroes_profile_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-warning text-decoration-none fw-semibold d-inline-flex align-items-center gap-1"
                        style={{ fontSize: '1rem' }}
                      >
                        View Profile <i className="bi bi-box-arrow-up-right"></i>
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {/* Compact Bio Section */}
              <div className="border-top pt-4">
                <div className="small text-muted-soft text-uppercase mb-3" style={{ letterSpacing: '0.5px', fontSize: '0.875rem' }}>
                  About
                </div>
                <p className="mb-0 text-white" style={{ lineHeight: '1.6', fontSize: '0.95rem', color: 'rgba(255, 255, 255, 0.9)' }}>
                  {player.bio}
                </p>
              </div>
              
              {/* Hire Information */}
              <div className="border-top pt-4 mt-4">
                <div className="text-center py-2">
                  <div className="text-danger fw-bold mb-2" style={{ fontSize: 'clamp(1rem, 2.5vw, 1.1rem)', lineHeight: '1.5' }}>
                    FOR HIRE THIS PLAYER
                  </div>
                  <div className="text-danger fw-bold" style={{ fontSize: 'clamp(0.95rem, 2.5vw, 1.05rem)', lineHeight: '1.5' }}>
                    CALL TO- 01788-083425
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer border-top d-flex justify-content-end">
              <button type="button" className="btn btn-outline-secondary" onClick={onHide}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PlayerProfileModal;
